import lodash from "lodash";
import moment from "moment";

import {
    createColumnDate,
    createColumnInput, createColumnInputNumber,
    createColumnSelect
} from "../../../../components/table/c-table.logic";
import store from "../../../../store";
import { createLoading } from "../../../../utils/loading";
import i18n from "../../../../i18n";
import { createManageDelete, createPage, createRowActionEdit, createRowActionView } from "../../../../utils/page";
import { conditionFormChecker, initUseForm, onClickDeleteAllClient, putField } from "../../../../utils/form";
import { createButtonCreate } from "../../../../utils/button";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { useForm as createUseForm } from "@ant-design-vue/use";
import { required, validateError } from "../../../../utils/validate";
import {
    createFieldAttachment,
    createFieldCheckbox,
    createFieldDate,
    createFieldInput, createFieldInputNumber,
    createFieldSelect
} from "../../../../components/input/template-builder/template-builder.logic";
import { modalStyleForm } from "../../../../utils/constant";
import { addModal } from "../../../../app.logic";
import { createConditionForm } from "./license-suspended-history.condition";
import { have } from "../../../../utils/menu-context";

export const callLawProvision = ({ searchQuery }) => store.dispatch("resource/v1/master/lawProvision/cacheFindAll", { searchQuery });
export const { isLoading, setIsLoading } = createLoading();
const LicenseSuspendedHistoryForm = defineAsyncComponent(() => import("./LicenseSuspendedHistoryForm"));

export const callAdministrativeCommandType = ({ searchQuery }) =>
    store
        .dispatch("resource/v1/master/lookup/cacheFindAllByGroup", {
            group: "ADMINISTRATIVE_COMMAND_TYPE",
            value: searchQuery
        });

export const createColumns = () => {
    const columns = [
        createColumnSelect({
            title: () => i18n.global.t("model.licenseSuspendedHistories.refCommandType"),
            dataIndex: "refCommandType.value",
            inputProp: () => ({
                list: (searchQuery) => callAdministrativeCommandType({
                    searchQuery,
                    refParentOperator: "=",
                    refParentId: null
                }),
                optLabel: "value"
            }),
            search: { name: "refCommandType.id" }
        }),
        createColumnInput({
            title: () => i18n.global.t("model.licenseSuspendedHistories.cmdNumber"),
            dataIndex: "cmdNumber"
        }),
        createColumnDate({
            title: () => i18n.global.t("model.licenseSuspendedHistories.cmdEffectiveDate"),
            dataIndex: "cmdEffectiveDate",
            align: "center"
        }),
        createColumnDate({
            title: () => i18n.global.t("model.licenseSuspendedHistories.cmdEndDate"),
            dataIndex: "cmdEndDate",
            align: "center"
        }),
        createColumnInputNumber({
            title: () => i18n.global.t("model.licenseSuspendedHistories.suspendedDuration"),
            dataIndex: "suspendedDuration",
            align: "center"
        })
    ];
    return columns;
};

const calSuspendedDuration = ({ modelRef }) => {
    if (modelRef.cmdEffectiveDate && modelRef.cmdEndDate) {
        const cmdEffectiveDate = moment(modelRef.cmdEffectiveDate, "YYYY-MM-DD");
        const cmdEndDate = moment(modelRef.cmdEndDate, "YYYY-MM-DD");
        const duration = moment.duration(cmdEndDate.diff(cmdEffectiveDate)).asDays();
        if (duration) {
            modelRef.suspendedDuration = duration;
        } else {
            modelRef.suspendedDuration = null;
        }
    } else {
        modelRef.suspendedDuration = null;
    }
};

export const initData = ({ props, data }) => {
    const { menuContext, viewOnly, itemsKey } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    data.isOfficer = have({
        menuContext: props.menuContext,
        keys: ["forLawyerAuditor", "forDofCenter", "forAuthorized"]
    });
    data.columns = createColumns({ props });
    data.page = createPage({ data: items });
    data.tableOptions = { search: { activeKey: null } };
    data.manages = [
        createManageDelete({
            menuContext,
            visible: () => true,
            onClick: ({ rowSelection }) => onClickDeleteAllClient({ rowSelection, useForm, items, key: itemsKey })
        })
    ];
    data.manageExtras = [];
    data.rowActions = [
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) =>
                openModalForm({
                    props: {
                        menuContext,
                        viewOnly,
                        setAppPageHeader: false,
                        form: record,
                        itemsList: items
                    }
                }),
            visible: () => !viewOnly && data.isOfficer
        }),
        createRowActionView({
            menuContext,
            onClick: ({ record }) =>
                openModalForm({
                    props: {
                        menuContext,
                        viewOnly: true,
                        setAppPageHeader: false,
                        form: record,
                        itemsList: items
                    }
                })
        })
    ];

    if (!viewOnly && data.isOfficer) {
        data.manageExtras.push({
            render: () => ({
                ...createButtonCreate({
                    visible: () => !viewOnly && data.isOfficer,
                    onClick: () => openModalForm({ props, items }),
                    options: { text: i18n.global.t("button.create") }
                })
            }),
            visible: () => !viewOnly
        });
    }

};

export const initForm = ({ props, data, refs }) => {
    data.form = createForm();
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    loadForm({ props, data, refs });
    loadDefaultForm({ props, data }).then(() => {
        data.fields = createFields({ props, data, refs, isLoading });
        data.conditionForms = createConditionForm();
    }).then(() => {
        initUseForm({ data, page: { data: [data?.useForm?.modelRef] } });
    });

};

export const createForm = () => {
    return {
        refCommandType: null,
        cmdEndDate: null,
        cmdEffectiveDate: null,
        commandNumber: null,
        suspendedDuration: null,
        attachments: [],
        administrativeCommandDetails: []
    };
};

export const createFormAdministrativeCommandDetail = (form = {}) => {
    return lodash.merge({
        id: null,
        refLaw: null,
        refLawProvision: null,
        isResult: false
    }, form);
};

export const createRules = () => {
    return {
        refCommandType: [required],
        cmdNumber: [required],
        cmdEffectiveDate: [required]
    };
};

export const createFields = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data, fields: [
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({
                    props,
                    data,
                    executeKey: "visibleField",
                    params: { field, model }
                }),
                key: `${prefix}refCommandType`,
                colProp: () => ({ xs: 24, md: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => `${i18n.global.t("model.licenseSuspendedHistories.refCommandType")}`,
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refCommandType,
                            "onUpdate:value": value => {
                                model.refCommandType = value;
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) => callAdministrativeCommandType({
                                searchQuery,
                                refParentOperator: "=",
                                refParentId: null
                            }),
                            optLabel: "value"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({
                    props,
                    data,
                    executeKey: "visibleField",
                    params: { field, model }
                }),
                key: `${prefix}cmdNumber`,
                colProp: () => ({ xs: 24, md: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => `${i18n.global.t("model.licenseSuspendedHistories.cmdNumber")}`,
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.cmdNumber,
                            "onUpdate:value": (value) => {
                                model.cmdNumber = value;
                            },
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({
                    props,
                    data,
                    executeKey: "visibleField",
                    params: { field, model }
                }),
                key: "cmdEffectiveDate",
                colProp: () => ({ xs: 24, md: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => i18n.global.t("model.licenseSuspendedHistories.cmdEffectiveDate"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: modelRef.cmdEffectiveDate,
                            "onUpdate:value": (value) => {
                                modelRef.cmdEffectiveDate = value;
                                if (modelRef.cmdEffectiveDate) {
                                    calSuspendedDuration({ modelRef });
                                }
                            }
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({
                    props,
                    data,
                    executeKey: "visibleField",
                    params: { field, model }
                }),
                key: "cmdEndDate",
                colProp: () => ({ xs: 24, md: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => i18n.global.t("model.licenseSuspendedHistories.cmdEndDate"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: modelRef.cmdEndDate,
                            "onUpdate:value": (value) => {
                                modelRef.cmdEndDate = value;
                                if (modelRef.cmdEffectiveDate) {
                                    calSuspendedDuration({ modelRef });
                                }
                            }
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({
                    props,
                    data,
                    executeKey: "visibleField",
                    params: { field }
                }),
                key: `${prefix}suspendedDuration`,
                colProp: () => ({ xs: 24, md: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => i18n.global.t("model.licenseSuspendedHistories.suspendedDuration"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.suspendedDuration,
                            comma: false,
                            maxDecimal: 10,
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            ...createFieldCheckboxLawProvision({
                props,
                data,
                isLoading,
                itemsKey: "administrativeCommandDetails"
            }),
            createFieldAttachment({
                key: `${prefix}attachments`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.licenseSuspendedHistories.attachment"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: modelRef.attachments,
                            "onUpdate:value": (value) => (modelRef.attachments = value),
                            disabled: conditionFormChecker({
                                props,
                                data,
                                executeKey: "inputPropField",
                                params: { field }
                            }),
                            multiple: true,
                            group: "LicenseSuspendedHistoriesAttachment"
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise
        .then(page => initUseForm({ data, page }))
        .finally(() => setIsLoading(false));
};


export const loadDefaultForm = ({ props, data }) => {
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    const promises = [
        callLawProvision({})
    ];
    return Promise.all(promises).then(arr => {
        data.lawProvisions = arr[0];
        /* -- Set Default Value -- */
        if ((!id && !form)) {
            data.lawProvisions.forEach(lawProvision => {
                const administrativeCmd = createFormAdministrativeCommandDetail({
                    refLawProvision: lawProvision,
                    refLaw: lawProvision?.refLaw
                });
                modelRef.administrativeCommandDetails.push(administrativeCmd);
            });
        }
    });
};


export const openModalForm = ({ props, items, callback }) => {
    const refs = reactive({ form: null });
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleForm), {
            width: "80%",
            okButtonProps: { disabled: props.viewOnly },
            header: i18n.global.t("model.button.create"),
            onOk: () => {
                refs.form.callOnClickModalOk()
                    .then((record) => {
                        callback?.();
                        if (items) {
                            items.push(record);
                        } else {
                            lodash.merge(props.form, record);
                        }
                        options.bind.onCancel();
                    });
            }
        }),
        render: () =>
            <LicenseSuspendedHistoryForm
                ref={
                    (e) => {
                        if (e) refs.form = e;
                    }
                }
                {...props}
            ></LicenseSuspendedHistoryForm>
    });
    return options;
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch((error) => {
            validateError({ error });
            throw error;
        });
};


export const createFieldCheckboxLawProvision = ({ props, data, itemsKey }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    const prefix = itemsKey ? `${itemsKey}.` : "";
    return items
        .map((item, index) => {
            if (!item?.refLawProvision) {
                return null;
            }
            const keyCheck = `${prefix}${index}.isResult`;
            return createFieldCheckbox({
                // visible: () => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field: { key: keyCheck }, items, item } }),
                visible: () => true,
                key: keyCheck,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 4 }, wrapperCol: { xs: 24, md: 20 } }),
                label: () => " ",
                inputProp: () => {
                    return lodash.merge(
                        {
                            checked: item?.isResult,
                            "onUpdate:checked": (checked) => {
                                item.isSave = true;
                                item.isResult = checked;
                            },
                            disabled: viewOnly,
                            text: () => `${item.refLawProvision.name}`
                        },
                        conditionFormChecker({
                            props,
                            data,
                            executeKey: "checkedProp",
                            params: { field: { key: keyCheck }, items, item }
                        })
                    );
                }
            });
        })
        .filter((field) => field);
};
